import React, { useCallback, useState } from "react";

import {
  CButton,
  CButtonGroup,
  CCol,
  CFormGroup,
  CInputCheckbox,
  CLabel,
  CRow,
} from "@coreui/react";

import { Spinner } from "react-bootstrap";
import Errors, { getFieldErrors } from "../../models/errors";
import { FieldErrors } from "../form/FieldErrors";
import { SUCCESS } from "../../utils/constants/tags";
import { errorAlert } from "../utils/messages";
import { createItem, ItemRequestStatus, updateItem } from "../../api/generics";
import UserTaxPayer from "../../models/user-tax-payer";
import User from "../../models/user";
import UserSelect from "../user/UserSelect";
import AnimatedCheckbox from "../checkbox/AnimatedCheckbox";
import { PERMISSION_GROUPS } from "../../auth/permissions";

interface UserTaxPayerFormProps {
  userTaxPayer?: UserTaxPayer;
  taxPayerId?: number;
  onCancel: () => void | Promise<void>;
  onSuccess: () => void | Promise<void>;
  clearOnSuccess?: boolean;
}

const UserTaxPayerForm: React.FC<UserTaxPayerFormProps> = ({
  userTaxPayer,
  taxPayerId,
  onCancel,
  onSuccess,
  clearOnSuccess,
}) => {
  // TODO: verify error with regular clearOnSuccess bool
  // eslint-disable-next-line
  const [shouldClearOnSuccess, _] = useState(clearOnSuccess);
  const [disabled, setDisabled] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Errors>({});

  const [editingUserTaxPayer, setEditingUserTaxPayer] = useState<UserTaxPayer>(
    userTaxPayer !== undefined ? userTaxPayer : {}
  );

  const onUserChange = (newUser: User | null) => {
    setEditingUserTaxPayer((prev) => {
      if (newUser !== null) {
        return { ...prev, userId: newUser.id, user: newUser };
      }
      return { ...prev, userId: undefined, user: undefined };
    });
  };

  const onIsSuperUserChange = (newIsSuperUser: boolean) => {
    setEditingUserTaxPayer((prev) => {
      return { ...prev, isSuperUser: newIsSuperUser };
    });
  };

  const onClose = () => {
    clearForm();
    onCancel();
  };

  const clearForm = () => {
    setEditingUserTaxPayer({});
  };

  const onSaveClick = useCallback(async () => {
    let requestPromise: Promise<ItemRequestStatus<UserTaxPayer>>;

    if (taxPayerId === undefined) {
      return;
    }

    const toSend = { ...editingUserTaxPayer, taxPayerId: taxPayerId };

    if (toSend.isSuperUser === undefined) {
      toSend.isSuperUser = false;
    }

    if (toSend.id === undefined) {
      requestPromise = createItem(
        `/tax_payers/${taxPayerId}/users/create/`,
        toSend
      );
    } else {
      requestPromise = updateItem(
        `/tax_payers/${taxPayerId}/users/${toSend.id}/`,
        toSend
      );
    }

    const requestStatus = await requestPromise;

    if (requestStatus.status !== SUCCESS) {
      let message = "Ha ocurrido un error!!";
      if (requestStatus.detail !== undefined) {
        message = requestStatus.detail;
      }
      errorAlert(message);
    } else {
      if (shouldClearOnSuccess) {
        setErrors({});
        clearForm();
      }

      onSuccess();
    }

    setErrors(requestStatus.errors!);
    setSubmitting(false);
    setDisabled(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingUserTaxPayer, taxPayerId]);

  const onPermissionsChange = useCallback(
    (permissionName: string, enabled: boolean) => {
      const newEditingItem = { ...editingUserTaxPayer };
      if (
        newEditingItem.permissions === undefined ||
        newEditingItem.permissions === null
      ) {
        newEditingItem.permissions = [];
      }

      const permissionIndex =
        newEditingItem.permissions.indexOf(permissionName);

      if (enabled && permissionIndex < 0) {
        newEditingItem.permissions.push(permissionName);
      }

      if (!enabled && permissionIndex >= 0) {
        const permissionIndex =
          newEditingItem.permissions.indexOf(permissionName);
        newEditingItem.permissions.splice(permissionIndex, 1);
      }

      setEditingUserTaxPayer(newEditingItem);
    },
    [editingUserTaxPayer]
  );

  const user =
    editingUserTaxPayer !== null && editingUserTaxPayer.user !== undefined
      ? editingUserTaxPayer.user
      : null;

  return (
    <>
      <CFormGroup>
        <CRow>
          <CCol md={2}>
            <CLabel>
              <span className="text-danger">*</span> Usuario
            </CLabel>
          </CCol>
          <CCol>
            <UserSelect value={user} onChange={onUserChange} />
            <FieldErrors
              errors={getFieldErrors("userId", errors) as string[]}
            ></FieldErrors>
          </CCol>
        </CRow>
      </CFormGroup>
      <CFormGroup>
        <CRow>
          <CCol md={2}>
            <CLabel>Super Usuario:</CLabel>
          </CCol>
          <CCol>
            <CRow>
              <AnimatedCheckbox
                value={editingUserTaxPayer.isSuperUser === true}
                onChange={onIsSuperUserChange}
              ></AnimatedCheckbox>
            </CRow>

            <FieldErrors
              errors={getFieldErrors("enabled", errors) as string[]}
            ></FieldErrors>
          </CCol>
        </CRow>
      </CFormGroup>
      <CRow className="mb-2">
        <CCol>
          <h5>
            <u>Permisos</u>
          </h5>
        </CCol>
      </CRow>
      <CFormGroup>
        {Array.from(PERMISSION_GROUPS.entries()).map((entry) => (
          <CRow key={entry[0]}>
            <CCol md={2}>
              <CLabel>{entry[0]}</CLabel>
            </CCol>
            <CCol md={10}>
              <CRow>
                {entry[1].map((value) => (
                  <CCol key={value.value} md={3}>
                    <CInputCheckbox
                      id={`permission_${value.value}`}
                      checked={
                        editingUserTaxPayer.permissions !== undefined &&
                        editingUserTaxPayer.permissions !== null &&
                        editingUserTaxPayer.permissions.indexOf(value.value) >=
                          0
                      }
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        onPermissionsChange(value.value, e.target.checked);
                      }}
                    ></CInputCheckbox>
                    <CLabel htmlFor={`permission_${value.value}`}>
                      {value.label}
                    </CLabel>
                  </CCol>
                ))}
              </CRow>
            </CCol>
          </CRow>
        ))}
      </CFormGroup>
      <CFormGroup className="float-right">
        <CButtonGroup>
          <CButton type="button" color="secondary" onClick={onClose}>
            Salir
          </CButton>
          <CButton
            className="btn btn-primary"
            disabled={disabled}
            onClick={onSaveClick}
          >
            {submitting ? (
              <Spinner
                animation="grow"
                style={{
                  height: "17px",
                  width: "17px",
                  marginTop: "auto",
                  marginBottom: "auto",
                  marginRight: "10px",
                }}
              />
            ) : (
              <></>
            )}
            {submitting ? "Guardando..." : "Guardar"}
          </CButton>
        </CButtonGroup>
      </CFormGroup>
    </>
  );
};

export default UserTaxPayerForm;
